// File: PreviousYearPapers.js
import React, { useState } from "react";
import { Container, Row, Col, Nav, Button, Modal, Card } from "react-bootstrap";
import {
  FaBookOpen,
  FaUniversity,
  FaBalanceScale,
  FaGavel,
  FaLaptopCode,
  FaShieldAlt,
  FaFileAlt,
  FaGlobeAsia,
  FaBrain,
} from "react-icons/fa";

const PreviousYearPapers = () => {
  const [activeKey, setActiveKey] = useState("central");
  const [showModal, setShowModal] = useState(false);
  const [selectedPaper, setSelectedPaper] = useState(null);

  const openModal = (paper) => {
    setSelectedPaper(paper);
    setShowModal(true);
  };

  const closeModal = () => setShowModal(false);

  const paperData = {
    central: {
      title: "Central Government Exams",
      description:
        "Practice and analyze previous year question papers for UPSC, SSC, IBPS, DRDO, ISRO, and other national-level exams.",
      papers: [
        {
          name: "UPSC Civil Services (IAS, IPS, IFS) – 2024, 2023, 2022",
          subject: "General Studies I, II, III, IV + Optional Subjects",
          icon: <FaUniversity />,
        },
        {
          name: "SSC CGL & CHSL – 2023, 2022, 2021",
          subject: "Quantitative Aptitude, Reasoning, English, GK",
          icon: <FaBalanceScale />,
        },
        {
          name: "IBPS & SBI PO – 2023, 2022, 2021",
          subject: "Banking Awareness, Quant, Reasoning, English",
          icon: <FaFileAlt />,
        },
        {
          name: "DRDO / ISRO Scientist – 2022, 2021",
          subject: "Engineering, Physics, General Aptitude",
          icon: <FaLaptopCode />,
        },
        {
          name: "CDS / NDA / CAPF – 2023, 2022",
          subject: "GK, English, Mathematics, Defence Awareness",
          icon: <FaShieldAlt />,
        },
      ],
    },
    state: {
      title: "State Government Exams",
      description:
        "Access state PSC, Technical, Education, and Police recruitment papers from all major states.",
      papers: [
        {
          name: "UPPSC / TNPSC / KPSC / APPSC – 2023, 2022",
          subject: "State GK, History, Polity, Geography",
          icon: <FaUniversity />,
        },
        {
          name: "State Engineering & Technical – 2023, 2022",
          subject: "Engineering Discipline Papers + State Policy",
          icon: <FaLaptopCode />,
        },
        {
          name: "State Police / SI / Constable Exams – 2023, 2022",
          subject: "Reasoning, GK, Physical Test, Law Basics",
          icon: <FaShieldAlt />,
        },
        {
          name: "Teacher Eligibility (TET/SET/CTET) – 2023, 2022",
          subject: "Pedagogy, Child Psychology, Subject Papers",
          icon: <FaBookOpen />,
        },
      ],
    },
    competitive: {
      title: "Competitive & Entrance Exams",
      description:
        "Prepare smarter with solved papers for top competitive exams — IIT-JEE, NEET, GATE, CDS, NDA, CAT, and more.",
      papers: [
        {
          name: "IIT JEE (Mains + Advanced) – 2024, 2023",
          subject: "Physics, Chemistry, Mathematics",
          icon: <FaLaptopCode />,
        },
        {
          name: "NEET UG – 2024, 2023",
          subject: "Biology, Physics, Chemistry",
          icon: <FaBookOpen />,
        },
        {
          name: "GATE – 2024, 2023, 2022",
          subject: "Engineering Subjects, Aptitude",
          icon: <FaBrain />,
        },
        {
          name: "CAT / XAT / CMAT – 2023, 2022",
          subject: "Quant, Verbal, Logical Reasoning, DI",
          icon: <FaBalanceScale />,
        },
        {
          name: "NDA / CDS / AFCAT – 2023, 2022",
          subject: "Maths, GK, English, Defence Awareness",
          icon: <FaShieldAlt />,
        },
      ],
    },
    general: {
      title: "Common General Studies & Aptitude",
      description:
        "Core subjects for all exams: strengthen your GS, aptitude, current affairs, and English through past papers.",
      papers: [
        {
          name: "General Studies – All Competitive Exams",
          subject: "History, Polity, Geography, Economics, Environment",
          icon: <FaGlobeAsia />,
        },
        {
          name: "Quantitative Aptitude",
          subject: "Arithmetic, Algebra, Data Interpretation, Speed Maths",
          icon: <FaBalanceScale />,
        },
        {
          name: "Reasoning & Mental Ability",
          subject: "Verbal & Non-Verbal Reasoning, Puzzles, Coding-Decoding",
          icon: <FaBrain />,
        },
        {
          name: "English Language & Comprehension",
          subject: "Grammar, Vocabulary, Reading Comprehension",
          icon: <FaBookOpen />,
        },
        {
          name: "Current Affairs (Monthly + Yearly Compilations)",
          subject: "National, International, Science & Tech, Economy",
          icon: <FaFileAlt />,
        },
      ],
    },
  };

  return (
    <Container fluid className="py-5 bg-light">
      <h2 className="text-center mb-4 fw-bold">
        <FaBookOpen className="me-2" />
        Previous Year Papers & Practice Hub
      </h2>
      <Row>
        {/* Sidebar */}
        <Col md={3} className="mb-4">
          <Nav
            variant="pills"
            className="flex-column"
            activeKey={activeKey}
            onSelect={(key) => setActiveKey(key)}
          >
            <Nav.Item>
              <Nav.Link eventKey="central">🏛 Central Govt Exams</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="state">🏢 State Govt Exams</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="competitive">🎯 Competitive & Entrance</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="general">📘 General Studies</Nav.Link>
            </Nav.Item>
          </Nav>
        </Col>

        {/* Papers Section */}
        <Col md={9}>
          <h4 className="fw-bold">{paperData[activeKey].title}</h4>
          <p>{paperData[activeKey].description}</p>
          <Row>
            {paperData[activeKey].papers.map((paper, index) => (
              <Col md={6} lg={4} key={index} className="mb-3">
                <Card className="shadow-sm h-100">
                  <Card.Body>
                    <h6 className="fw-bold text-primary">
                      {paper.icon} {paper.name}
                    </h6>
                    <p className="small text-muted">{paper.subject}</p>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => openModal(paper)}
                    >
                      View Details
                    </Button>{" "}
                    <Button
                      variant="primary"
                      size="sm"
                      href="/enroll"
                      className="ms-2"
                    >
                      Enroll
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Col>
      </Row>

      {/* Modal */}
      <Modal show={showModal} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{selectedPaper?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>Subjects Covered:</strong> {selectedPaper?.subject}
          </p>
          <p>
            Access detailed question analysis, solved papers, and expert
            commentary for each year’s exam. Strengthen conceptual clarity and
            understand examiner patterns.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Close
          </Button>
          <Button variant="outline-success">View / Download PDF</Button>
          <Button variant="primary" href="/enroll">
            Enroll for Guidance
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PreviousYearPapers;
